import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./LandingPage.css";
import LogoBlack from "./Vybly Logo (Black).png";
import { ListChecks, Bolt, Search, Funnel, MessageSquareText, RefreshCw, Network, Sparkles, BarChart3, CircleSlash2, PenLine, Users } from "lucide-react";

/* Tiny inline icons for cards (kept lightweight) */
const I = {
  mindmap: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="6" cy="8" r="2" fill="currentColor" />
      <circle cx="12" cy="12" r="2" fill="currentColor" />
      <circle cx="18" cy="6" r="2" fill="currentColor" />
      <circle cx="18" cy="18" r="2.4" fill="currentColor" />
      <path d="M7.7 9.5 10 11m3.7 2.6 3.8 3.2M16 7l1.3 3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  coach: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21 4v6h-6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M12 4a8 8 0 1 0 7.5 5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="9" cy="12" r="1.2" fill="currentColor" />
      <circle cx="13" cy="12" r="1.2" fill="currentColor" />
      <path d="M8.8 15.6c1.7 1.6 4.7 1.6 6.4 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  insights: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 18V6M10 18V9M16 18V4M20 18V12M3 20h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  vetos: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7 7l10 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  blurbs: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 5h16v10H7l-3 3V5z" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 9h8M8 12h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l7 3v5c0 5-3.6 8.2-7 10-3.4-1.8-7-5-7-10V6l7-3z" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M9.5 12.5l2 2 3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

/* --- Responsive hexes: 2 clusters (6 hexes total) --------------------- */
const Hex = ({ color, className = "" }) => (
  <svg
    className={`hex ${className}`}
    viewBox="0 0 110 130"
    aria-hidden="true"
    preserveAspectRatio="xMidYMid meet"
  >
    <polygon points="55,0 110,32 110,98 55,130 0,98 0,32" fill={color} />
  </svg>
);

const HeroHexClusters = () => (
  <div className="hex-layer" aria-hidden="true">
    {/* Cluster 1: top-right */}
    <div className="hex-cluster cluster--tr">
      <Hex color="#FFBF00" className="hex a" />
      <Hex color="#FFBF00" className="hex b" />
      <Hex color="#6EC3C2" className="hex c" />
    </div>
    {/* Cluster 2: bottom-right */}
    <div className="hex-cluster cluster--br">
      <Hex color="#FFBF00" className="hex a" />
      <Hex color="#6EC3C2" className="hex b" />
      <Hex color="#FFBF00" className="hex c" />
    </div>
  </div>
);

export default function LandingPage() {
  const navigate = useNavigate();

  const jumpToQuiz = useCallback(() => {
    const el = document.getElementById("quiz-section");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      // No anchor? Hard scroll to page bottom instead of routing to /signup
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    }
  }, []);

  // Intercept any legacy /signup links (e.g., navbar CTA) and route to quiz instead
  useEffect(() => {
    const handler = (e) => {
      const t = e.target.closest("a");
      if (!t) return;
      const href = t.getAttribute("href") || "";
      if (href.includes("/signup")) {
        e.preventDefault();
        jumpToQuiz();
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [jumpToQuiz]);

  return (
    <main className="lp">

      {/* ===== HERO (exact-art inline SVG; waves untouched) ===== */}
      <section className="sec heroExact" aria-labelledby="hero-title">
        <svg className="heroExact__art" viewBox="0 0 1440 645" preserveAspectRatio="none" aria-hidden="true">
          <rect width="1440" height="645" fill="#0F1B27" />
          <radialGradient id="vign" cx="78%" cy="0%" r="55%">
            <stop offset="0%" stopColor="#000" stopOpacity="0.35" />
            <stop offset="60%" stopColor="#000" stopOpacity="0" />
          </radialGradient>
          <rect width="1440" height="645" fill="url(#vign)" />
          {/* waves (unchanged) */}
          <path fill="#1F6B74" d="M0,0 C240,70 420,0 720,40 C1020,80 1200,20 1440,60 L1440,160 0,160 Z" />
          <path fill="#124252" d="M0,120 C240,180 420,80 720,120 C1020,160 1200,100 1440,140 L1440,320 0,320 Z" transform="translate(0,20)" opacity="0.98" />
          {/* bottom gold wave (unchanged) */}
          <path
            fill="#FFBF00"
            d="M1440,645 L1440,500 C1230,520 1100,600 930,610 C780,620 660,590 530,576 C350,557 230,595 0,575 L0,645 Z"
          />
        </svg>

        <div className="container heroExact__wrap">
          <div className="heroExact__content">
            <h1 id="hero-title" className="h1">Your Space to Think, Create, and Connect</h1>
            <p className="heroExact__sub">
              You bring the spark and the 20 tabs open in your head, we bring the tools to make things happen.
            </p>
            <p className="heroExact__lede">
              We’re here for curiosity, banter, breakthroughs, and the occasional “holy sh*t, we actually solved it.”
            </p>
            <div className="cta-row">
              <button className="btn btn-gold" onClick={jumpToQuiz}>Jump In Free</button>
            </div>
            {/* research-backed / no-cc note */}
            {/* <p style={{marginTop: 8, opacity: 0.9}}>
              We start with a quick, research-backed DeepDive to tailor your experience. No credit card required.
            </p> */}
          </div>
        </div>

        {/* The only hexes are these two responsive clusters (6 total) */}
        <HeroHexClusters />

        <svg className="heroExact__divider" viewBox="0 0 1440 120" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0,80 C240,140 420,40 720,80 C1020,120 1200,60 1440,100 L1440,120 L0,120 Z" fill="#ffffff" />
        </svg>
      </section>

      {/* ===== Micro-proof strip (quotes) ===== */}
      <section className="sec section--clean" aria-label="Social proof">
        <div className="container">
          <div className="proof-strip">
            <figure className="proof-quote">
              <blockquote>“I used Vybly to prep for a tough meeting and it legit boosted my confidence.”</blockquote>
              <figcaption>— Alejandro</figcaption>
            </figure>
            <figure className="proof-quote">
              <blockquote>“The solo toolkit showed me solutions I never would have come up with by myself. Seriously useful.”</blockquote>
              <figcaption>— Taylor</figcaption>
            </figure>
            <figure className="proof-quote">
              <blockquote>“Feels like having a therapist, coach, and hype squad in my pocket (minus the awkwardness).”</blockquote>
              <figcaption>— Priya</figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* ===== Why Vybly? ===== */}
      <section className="sec section--clean" aria-labelledby="why-title">
        <div className="container narrow">
          <div className="eyebrow-pill">Why Vybly?</div>
          <h2 id="why-title" className="h2">Why Vybly?</h2>
          <p className="lead">
            You already talk to friends, scroll Reddit, and get lost in DMs. But Vybly is where:
          </p>

          <ul className="bullet-cards">
            <li className="bullet-card">Your random 2 AM thought can turn into a plan by tomorrow.</li>
            <li className="bullet-card">You can test-drive ideas in minutes, not months.</li>
            <li className="bullet-card">You actually feel seen — because the people here speak your mental language.</li>
          </ul>

          <div className="callout">
            <p>Not another self-help grind. No lectures. No homework.</p>
          </div>

          <p className="strap">
            <strong>Smart tools, simple flow.</strong> Visual mind maps, decision filters, personal insight trackers — all in one place.
          </p>
        </div>
      </section>

      {/* ===== How It Works ===== */}
      <section className="sec steps-sec" aria-labelledby="how-title">
        <div className="container">
          <div className="eyebrow-pill eyebrow-pill--teal">How It Works</div>
          <h2 id="how-title" className="h2">How It Works (3 Steps)</h2>

          <div className="steps-grid">
            <article className="step-card">
              <span className="step-badge">1</span>
              <h3>Pick a starting point</h3>
              <p>Use our DeepDives to get to know yourself, drop in a question, or explore trending topics.</p>
            </article>
            <article className="step-card">
              <span className="step-badge">2</span>
              <h3>Play with ideas</h3>
              <p>Collaborate on Hive-Mind or use our solo tool, Frameworks, to get from messy thought to “a-ha” moment.</p>
            </article>
            <article className="step-card">
              <span className="step-badge">3</span>
              <h3>See the connections</h3>
              <p>Watch your ideas evolve and connect with people who think like you (Hive-Mind coming soon).</p>
            </article>
          </div>
        </div>
      </section>

      {/* ===== Wins Grid (benefit outcomes) ===== */}
      <section className="sec feature-slab" aria-label="What you’ll get">
        <div className="container" style={{display:'grid', gap:'18px'}}>
          <div className="eyebrow-pill eyebrow-pill--teal wins-pill">Wins</div>
          <h2 className="h2">Outcomes people get with Vybly</h2>

          <div className="wins-grid">
            {/* 1) Get your head straight */}
            <article className="wins-card">
              <div className="wins-head">
                <span className="wins-icon">
                  <ListChecks size={22} strokeWidth={1.8} />
                </span>
                <h3>Get your head straight</h3>
              </div>
              <p>Turn the 20 browser tabs in your brain into one plan you can actually finish today.</p>
            </article>

            {/* 2) Break the jam */}
            <article className="wins-card">
              <div className="wins-head">
                <span className="wins-icon">
                  <Bolt size={22} strokeWidth={1.8} />
                </span>
                <h3>Break the jam</h3>
              </div>
              <p>We’ll call out the exact mental roadblock and hand you the crowbar to get past it.</p>
            </article>

            {/* 3) Spot your tells */}
            <article className="wins-card">
              <div className="wins-head">
                <span className="wins-icon">
                  <Search size={22} strokeWidth={1.8} />
                </span>
                <h3>Spot your tells</h3>
              </div>
              <p>See the plays you keep running — and the blind spots that keep tripping you.</p>
            </article>

            {/* 4) Kill the noise */}
            <article className="wins-card">
              <div className="wins-head">
                <span className="wins-icon">
                  <Funnel size={22} strokeWidth={1.8} />
                </span>
                <h3>Kill the noise</h3>
              </div>
              <p>Veto the bad ideas fast so only the worth-it ones survive.</p>
            </article>

            {/* 5) Say it so it sticks */}
            <article className="wins-card">
              <div className="wins-head">
                <span className="wins-icon">
                  <MessageSquareText size={22} strokeWidth={1.8} />
                </span>
                <h3>Say it so it sticks</h3>
              </div>
              <p>Practice scenarios like interviews or hard conversations instead of talking to yourself in the shower.</p>
            </article>

            {/* 6) Keep the roll going */}
            <article className="wins-card">
              <div className="wins-head">
                <span className="wins-icon">
                  <RefreshCw size={22} strokeWidth={1.8} />
                </span>
                <h3>Keep the roll going</h3>
              </div>
              <p>Pick up right where you left off so progress isn’t a one-day miracle.</p>
            </article>
          </div>

          <div className="center">
            <button className="btn btn-gold" onClick={jumpToQuiz}>Start Free →</button>
          </div>
        </div>
      </section>

      {/* ===== Coming Soon: Hive-Mind (up-leveled visual) ===== */}
      <section className="sec section--soft" aria-labelledby="hm-title">
        <div className="container narrow">
          <div className="eyebrow-pill">Coming Soon</div>

          {/* Elevated, on-brand card with subtle motion & pattern */}
          <div
            style={{
              position: "relative",
              border: "1px solid #e9edf2",
              borderRadius: 16,
              padding: 24,
              overflow: "hidden",
              boxShadow: "0 14px 50px rgba(0,0,0,.08)",
              background:
                "radial-gradient(1200px 400px at 80% -10%, rgba(110,195,194,.18), transparent 60%), linear-gradient(180deg, rgba(232,244,243,.65), rgba(232,244,243,.28))",
            }}
          >
            {/* decorative nodes pattern */}
            <svg
              aria-hidden="true"
              width="420"
              height="220"
              viewBox="0 0 420 220"
              style={{ position: "absolute", right: -40, top: -20, opacity: 0.25 }}
            >
              <g fill="none" stroke="#0F1B27" strokeOpacity="0.25" strokeWidth="1.25">
                <circle cx="60" cy="60" r="12" />
                <circle cx="160" cy="40" r="8" />
                <circle cx="260" cy="70" r="10" />
                <circle cx="330" cy="30" r="6" />
                <circle cx="380" cy="90" r="9" />
                <path d="M60 60L160 40L260 70L330 30L380 90" />
              </g>
            </svg>

            {/* soft animated glow */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: "-40% -20% auto auto",
                width: 260,
                height: 260,
                borderRadius: "50%",
                background: "radial-gradient(closest-side, rgba(255,191,0,.22), transparent 70%)",
                filter: "blur(16px)",
                animation: "hmFloat 6s ease-in-out infinite",
              }}
            />
            <style>{`@keyframes hmFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(8px)}}`}</style>

            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <span
                style={{
                  display: "grid",
                  placeItems: "center",
                  width: 46,
                  height: 46,
                  borderRadius: 12,
                  background: "linear-gradient(180deg, #e8f4f3, #d8efec)",
                  color: "#0f1b27",
                  flex: "0 0 46px",
                }}
                aria-hidden="true"
              >
                <Users size={22} strokeWidth={1.8} />
              </span>
              <h3 id="hm-title" className="h2" style={{ margin: 0 }}>“Your brain + other brains = Hive-Mind.”</h3>
            </div>

            <p>Imagine jumping into a live or async session with people working on eerily similar challenges.</p>
            <p>One shared map. Everyone contributing, voting, and refining until the answer clicks.</p>
            <p>That’s Hive-Mind — launching soon to turn solo problem-solving into collective genius.</p>

            <div className="spaced">
              <p>Got an idea to pressure-test?<br />A decision you’ve been circling for weeks?<br />A “what if” you can’t shake?</p>
            </div>
            <p>You’ll have a crowd that maps it out with you. Our job is to have you leave with the clarity you didn’t have when you walked in.</p>

            <div className="cta-row" style={{ marginTop: 10 }}>
              <button className="btn btn-outline" onClick={jumpToQuiz}>Join Now</button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Get in early ===== */}
      <section className="sec feature-slab" aria-labelledby="early-title">
        <div className="container narrow">
          <div className="eyebrow-pill eyebrow-pill--teal">Early Access</div>
          <h2 id="early-title" className="h2">Get in early.</h2>

        <ul className="pill-list">
            <li>Use Vybly’s frameworks now.</li>
            <li>Be first in Hive-Mind rooms.</li>
            <li>Help shape the platform.</li>
            <li>Your progress carries over into group mode.</li>
          </ul>

          <p className="muted center-note">
            Plus, our tools are just as useful solo — you’ll leave each session with more clarity than you had going in.
          </p>
        </div>
      </section>

      {/* ===== Toolkit ===== */}
      <section className="sec toolkit" aria-labelledby="toolkit-title">
        <div className="container">
          <div className="eyebrow-pill eyebrow-pill--gold">Tools</div>
          <h2 id="toolkit-title" className="h2">Your solo toolkit. Supercharged.</h2>
          <p className="lead">
            While the Hive-Mind community cooks in the background, you’ve got instant access to Vybly’s individual frameworks — each designed to turn messy thoughts into solid moves, and rolled into one powerful tool.
          </p>

          <div className="cards-grid">
            <article className="tool-card">
              <div className="tool-head">
                <span className="tool-icon i-blue"><Network size={22} strokeWidth={1.8} /></span>
                <h3>MindMap</h3>
              </div>
              <p>
                Untangle messy thoughts and see connections you didn’t know existed. <strong>Like a whiteboard for your brain — but smarter.</strong>
              </p>
            </article>

            <article className="tool-card">
              <div className="tool-head">
                <span className="tool-icon i-purple"><Sparkles size={22} strokeWidth={1.8} /></span>
                <h3>Coach</h3>
              </div>
              <p>
                AI prompts that get you unstuck without the “toxic positivity.” <strong>Think: a sharp friend who asks better questions.</strong>
              </p>
            </article>

            <article className="tool-card">
              <div className="tool-head">
                <span className="tool-icon i-teal"><BarChart3 size={22} strokeWidth={1.8} /></span>
                <h3>Insights</h3>
              </div>
              <p>Your mental patterns, visualized — so you know your strong plays and blind spots.</p>
            </article>

            <article className="tool-card">
              <div className="tool-head">
                <span className="tool-icon i-orange"><CircleSlash2 size={22} strokeWidth={1.8} /></span>
                <h3>Vetos</h3>
              </div>
              <p>
                When you have too many options, this tool makes the cuts for you. <strong>No more analysis paralysis.</strong>
              </p>
            </article>

            <article className="tool-card">
              <div className="tool-head">
                <span className="tool-icon i-indigo"><PenLine size={22} strokeWidth={1.8} /></span>
                <h3>Blurbs</h3>
              </div>
              <p>Practice scenarios ahead of time. So you can confidently get into the flow that is right for you.</p>
            </article>
          </div>

          <div className="center">
            <button className="btn btn-gold" onClick={jumpToQuiz}>Start Free →</button>
          </div>
        </div>
      </section>

      {/* ===== Who It’s For ===== */}
      <section className="sec section--clean" aria-labelledby="who-title">
        <div className="container narrow">
          <div className="eyebrow-pill">Who It’s For</div>
          <h2 id="who-title" className="h2">Who It’s For</h2>
          <ul className="tick-list">
            <li>The over-thinker who can’t stop connecting dots.</li>
            <li>The action-taker who hates wasting time.</li>
            <li>The quietly curious who wants deeper convos without the small-talk fluff.</li>
          </ul>
        </div>
      </section>

      {/* ===== Data, Privacy & Security ===== */}
      <section className="sec dp" aria-labelledby="trust-title">
        <div className="container">
          <div className="eyebrow-pill eyebrow-pill--gold">Trust</div>
          <h2 id="trust-title" className="h2 h2-gold">Data, Privacy & Security</h2>
        </div>
        <div className="container dp-grid">
          <article className="dp-card">
            <span className="shield-icon">{I.shield}</span>
            <h3>We never sell your data</h3>
            <p>Your activity isn’t for sale, and it isn’t shared with other users unless you choose to share it.</p>
          </article>
          <article className="dp-card">
            <span className="shield-icon">{I.shield}</span>
            <h3>Anonymity options</h3>
            <p>Participate without identifying information if you want to. Your voice, your control.</p>
          </article>
          <article className="dp-card">
            <span className="shield-icon">{I.shield}</span>
            <h3>Encryption & 2FA</h3>
            <p>We encrypt data in transit and at rest and support two-factor authentication for account security.</p>
          </article>
          <article className="dp-card">
            <span className="shield-icon">{I.shield}</span>
            <h3>Privacy by design</h3>
            <p>We minimize what we keep and separate personal details from what you create. You decide what to reveal.</p>
          </article>
        </div>
      </section>

      {/* ===== Bottom strip ===== */}
      {/* QUIZ ANCHOR: put the anchor right above the bottom strip so CTAs land on the quiz */}
      <div id="quiz-section" style={{ position: "relative", top: "-64px" }} />
      <section className="bottom bottom--black">
        <div className="container bottom-inner">
          <nav className="bottom-nav">
            <a onClick={() => navigate('/about-us')}>About</a>
            <a onClick={() => navigate('/faq')}>FAQ</a>
            <a onClick={() => navigate('/privacy')}>Privacy</a>
            <a onClick={() => navigate('/terms')}>Terms</a>
          </nav>
          <p className="bottom-line">You’ve got the thoughts. We’ve got the space.</p>
          <button className="btn btn-gold" onClick={jumpToQuiz}>Jump In Free</button>
        </div>
      </section>
    </main>
  );
}
